import { DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Columns } from 'lucide-react';
import { leadsService } from '../../../services/leads.service';
import { productsService } from '../../../services/products.service';
import { useKanban } from '../../../hooks/useLeads';
import { useProduct } from '../../../contexts/ProductContext';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { toast } from '../../../ui';
import { StageColumn } from './StageColumn';
import { LeadCard, type LeadCardData } from './LeadCard';

interface Props {
  onLeadClick: (id: number) => void;
}

interface KanbanColumnDto {
  stage?: { id: number; name: string; type?: string };
  stageId?: number;
  leads: LeadCardData[];
  count?: number;
}

export function PipelineBoard({ onLeadClick }: Props) {
  const { selectedProductId } = useProduct();
  const queryClient = useQueryClient();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const { data: stagesData } = useQuery({
    queryKey: ['product', 'stages', selectedProductId],
    queryFn: () => productsService.getStages(selectedProductId!),
    enabled: !!selectedProductId,
  });
  const stages: { id: number; name: string; type?: string }[] =
    stagesData?.data || stagesData || [];

  const { data: kanbanData, isLoading } = useKanban(selectedProductId!);

  const moveStageMutation = useMutation({
    mutationFn: ({ id, stageId }: { id: number; stageId: number }) =>
      leadsService.moveStage(id, stageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads', 'kanban', selectedProductId] });
      toast.success('Lead movido');
    },
    onError: () => {
      toast.error('Erro ao mover lead');
    },
  });

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const leadId = Number(active.id);
    const stageId = Number(over.id);
    if (!stageId || Number.isNaN(leadId)) {
      return;
    }
    moveStageMutation.mutate({ id: leadId, stageId });
  }

  if (!selectedProductId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-ink-tertiary">
        <Columns className="w-12 h-12" />
        <p className="text-base font-medium">Selecione um produto para ver o pipeline</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const columns: KanbanColumnDto[] = kanbanData?.data?.columns || kanbanData?.columns || [];

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="flex gap-3 overflow-x-auto pb-2 min-h-[60vh]">
        {stages.map((stage) => {
          const column = columns.find((c) => c.stageId === stage.id || c.stage?.id === stage.id);
          const stageLeads: LeadCardData[] = column?.leads ?? [];
          return (
            <StageColumn
              key={stage.id}
              stageId={stage.id}
              title={stage.name}
              count={column?.count ?? stageLeads.length}
            >
              {stageLeads.map((lead) => (
                <LeadCard
                  key={lead.id}
                  lead={lead}
                  stageType={stage.type}
                  onClick={() => onLeadClick(lead.id)}
                />
              ))}
              {stageLeads.length === 0 && (
                <div className="flex items-center justify-center py-8 text-xs text-ink-tertiary">
                  Sem leads
                </div>
              )}
            </StageColumn>
          );
        })}
      </div>
    </DndContext>
  );
}
